import { Box, Flex, Separator, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useState } from 'react';

import { VoiceWave } from '#modules/finder/features/posts/components/Post/VoiceIndicator/VoiceWave';
import { ELEMENT_VOLUME_SUPPORTED } from '#features/media-window/audioRouting';
import { MicOffIcon } from '#features/ui';
import { Avatar } from '#features/ui/components/Avatar';
import { VoiceModerationDialog } from '#features/voice/components/VoiceModeration';
import { useVoiceStore, type VoiceMember } from '#features/voice/model/voiceStore';

type VoiceParticipantRowProps = {
  member: VoiceMember;
  /** Я сам — своей громкости в списке нет, регулировать себя бессмысленно. */
  isSelf?: boolean;
  onMuteForMe?: (userId: string, muted: boolean) => void;
  onVolumeChange?: (userId: string, volume: number) => void;
  /**
   * Действия автора сессии. Приходят только автору и только на чужие строки — у остальных меню
   * не рендерится вовсе, чтобы не сообщать о власти, которой у них нет.
   */
  onMuteForAll?: (userId: string) => void;
  onRemove?: (userId: string) => void;
};

/**
 * Строка участника в развёрнутом виджете. Говорящий подсвечивается фоном и волной — цвет строки
 * при этом не меняется, чтобы список не мигал при каждом «ага» в разговоре.
 *
 * Действия раскрываются прямо в строке, а не всплывающим меню. Окно разговора само по себе висит
 * поверх страницы у нижней кромки экрана: всплывающий слой поверх него ложится на его же
 * содержимое, а на телефоне ещё и оставляет для пальца ползунок в несколько пикселей. Раскрытие на
 * месте — один слой, одна ширина, одинаковое поведение в вебе и в приложении.
 */
export const VoiceParticipantRow = ({
  member,
  isSelf,
  onMuteForMe,
  onVolumeChange,
  onMuteForAll,
  onRemove,
}: VoiceParticipantRowProps) => {
  const { t } = useLingui();
  const volume = useVoiceStore((s) => s.localVolumes[member.userId] ?? 1);
  const mutedForMe = useVoiceStore((s) => s.localMuted[member.userId] ?? false);
  const [pendingAction, setPendingAction] = useState<'muteForAll' | 'remove' | null>(null);
  const [expanded, setExpanded] = useState(false);

  /**
   * Имя приходит из токена LiveKit на секунду-две позже состава от сервера. До этого показывать
   * `userId` нельзя: человек видит в списке чужой UUID и читает его как чей-то ник. Пока имени нет —
   * место под него держит заглушка.
   */
  const name = member.displayName || null;
  const label = name ?? t`post:voice.widget.unknownMember`;
  const canModerate = !isSelf && Boolean(onMuteForAll || onRemove);
  const hasActions = !isSelf && Boolean(onMuteForMe || canModerate);
  const isOpen = expanded && hasActions;

  return (
    <Box
      data-testid="voice-participant"
      data-self={isSelf ? 'true' : 'false'}
      // Состояние микрофона наружу строкой: это единственное место, где видно ФАКТ (что публикует
      // медиасервер), а не намерение кнопки. Ими же e2e и проверяет, что вошедшего слышно.
      data-muted={member.muted ? 'true' : 'false'}
      borderRadius="lg"
      bg={isOpen || member.speaking ? 'bg.muted' : 'transparent'}
    >
      <Flex
        as={hasActions ? 'button' : 'div'}
        w="100%"
        align="center"
        gap={2.5}
        px={2}
        py={1.5}
        borderRadius="lg"
        textAlign="left"
        transition="background 150ms cubic-bezier(.32,.72,0,1)"
        aria-expanded={hasActions ? isOpen : undefined}
        aria-label={
          hasActions ? `${label} – ${t`post:voice.widget.participantActions`}` : undefined
        }
        onClick={hasActions ? () => setExpanded((open) => !open) : undefined}
      >
        <Box
          flex="none"
          borderRadius="full"
          opacity={member.connected ? 1 : 0.55}
          boxShadow={
            member.speaking ? '0 0 0 2px var(--chakra-colors-status-live-solid)' : undefined
          }
        >
          <Avatar size="xs" name={name ?? undefined} src={member.avatar ?? undefined} />
        </Box>

        {name ? (
          <Text
            textStyle="label"
            flex={1}
            minW={0}
            color={member.muted || mutedForMe ? 'fg.muted' : 'fg'}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {name}
          </Text>
        ) : (
          <Box flex={1} minW={0}>
            <Box w="88px" maxW="100%" h="9px" borderRadius="full" bg="bg.emphasized" />
          </Box>
        )}

        {member.host ? (
          <Text textStyle="micro" color="fg.muted" flex="none" letterSpacing="0.04em">
            {t`post:voice.widget.host`}
          </Text>
        ) : null}

        {member.speaking ? <VoiceWave height={12} /> : null}
        {member.muted ? (
          <Box color="fg.muted" flex="none" lineHeight={0}>
            <MicOffIcon fontSize="15px" />
          </Box>
        ) : null}
        {/* Кого я не слышу — видно, не раскрывая строку: иначе «почему он молчит» проверяется
            перебором всех участников. */}
        {mutedForMe ? (
          <Box color="fg.subtle" flex="none" lineHeight={0}>
            <SpeakerOffGlyph />
          </Box>
        ) : null}

        {hasActions ? (
          <Box
            flex="none"
            color="fg.muted"
            lineHeight={0}
            transform={isOpen ? 'rotate(180deg)' : undefined}
            transition="transform 150ms cubic-bezier(.32,.72,0,1)"
          >
            <ChevronGlyph />
          </Box>
        ) : null}
      </Flex>

      {isOpen ? (
        <Box px={2} pb={2}>
          {onMuteForMe ? (
            <ActionRow
              icon={mutedForMe ? <SpeakerGlyph /> : <SpeakerOffGlyph />}
              label={mutedForMe ? t`post:voice.widget.unmuteForMe` : t`post:voice.widget.muteForMe`}
              onClick={() => onMuteForMe(member.userId, !mutedForMe)}
            />
          ) : null}

          {/* Ползунок — только там, где громкость элемента вообще слушается браузером. */}
          {onVolumeChange && ELEMENT_VOLUME_SUPPORTED ? (
            <Flex align="center" gap={2.5} px={1.5} py={2} opacity={mutedForMe ? 0.4 : 1}>
              <Box
                as="input"
                // @ts-expect-error — нативный input внутри Box: Chakra не типизирует его пропсы
                type="range"
                min={0}
                max={100}
                value={Math.round(volume * 100)}
                disabled={mutedForMe}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onVolumeChange(member.userId, Number(event.target.value) / 100)
                }
                flex={1}
                minW={0}
                accentColor="var(--chakra-colors-purple-solid)"
                aria-label={t`post:voice.widget.volumeFor`}
              />
              <Text
                textStyle="xs"
                color="fg.muted"
                fontVariantNumeric="tabular-nums"
                flex="none"
                w="34px"
                textAlign="right"
              >
                {Math.round(volume * 100)}%
              </Text>
            </Flex>
          ) : null}

          {canModerate ? (
            <>
              <Separator mx={1.5} my={1} />
              <Text textStyle="label" color="fg.muted" px={1.5} pt={1} pb={0.5}>
                {t`post:voice.moderation.asHost`}
              </Text>
              {onMuteForAll ? (
                <ActionRow
                  icon={<MicOffIcon fontSize="15px" />}
                  label={t`post:voice.moderation.muteForAll`}
                  onClick={() => setPendingAction('muteForAll')}
                />
              ) : null}
              {/* Красным окрашен только текст: плашка превратила бы редкое действие в самое
                  заметное в строке. */}
              {onRemove ? (
                <ActionRow
                  icon={<RemoveGlyph />}
                  label={t`post:voice.moderation.remove`}
                  tone="fg.error"
                  onClick={() => setPendingAction('remove')}
                />
              ) : null}
            </>
          ) : null}
        </Box>
      ) : null}

      <VoiceModerationDialog
        action={pendingAction}
        displayName={label}
        avatar={<Avatar size="sm" name={name ?? undefined} src={member.avatar ?? undefined} />}
        onClose={() => setPendingAction(null)}
        onConfirm={() => {
          if (pendingAction === 'remove') {
            onRemove?.(member.userId);
          }
          if (pendingAction === 'muteForAll') {
            onMuteForAll?.(member.userId);
          }
          setPendingAction(null);
        }}
      />
    </Box>
  );
};

/** Пункт раскрытой строки: во всю ширину и в 40px высотой — под палец, а не под курсор. */
const ActionRow = ({
  icon,
  label,
  tone,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  tone?: string;
  onClick: () => void;
}) => (
  <Flex
    as="button"
    w="100%"
    align="center"
    gap={2.5}
    minH="40px"
    px={1.5}
    borderRadius="md"
    color={tone ?? 'fg.secondary'}
    textAlign="left"
    _hover={{ bg: 'bg.emphasized' }}
    onClick={onClick}
  >
    <Box flex="none" lineHeight={0} w="15px">
      {icon}
    </Box>
    <Text textStyle="xs">{label}</Text>
  </Flex>
);

const ChevronGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const RemoveGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16 17l5-5-5-5M21 12H9M13 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h7" />
  </svg>
);

const SpeakerGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 5L6 9H2v6h4l5 4V5z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7" />
  </svg>
);

const SpeakerOffGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 2l20 20" />
    <path d="M11 5L6 9H2v6h4l5 4V5z" />
  </svg>
);
